/** The site operator's page: numbers, all teams and all users (including account disabling). */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../api';
import { ErrorBox, Panel, fmtBytes, fmtDate } from '../components';
import { t } from '../i18n';

interface Overview {
  teams: number;
  users: number;
  signups_7d: number;
  signups_30d: number;
  active_7d: number;
  projects: number;
  issues: number;
  storage_bytes: number;
}
interface TeamRow {
  id: number;
  name: string;
  created_at: string;
  owner: string | null;
  owner_email: string | null;
  members: number;
  projects: number;
  issues: number;
  storage_bytes: number;
}
interface UserRow {
  id: number;
  username: string;
  realname: string;
  email: string;
  email_confirmed: boolean;
  enabled: boolean;
  site_admin: boolean;
  created_at: string;
  last_visit: string | null;
  teams: number;
}

export function OperatorPage() {
  const [tab, setTab] = useState<'teams' | 'users'>('teams');
  const overview = useQuery({ queryKey: ['op-overview'], queryFn: () => api.get<Overview>('/api/operator/overview') });
  const o = overview.data;
  return (
    <div className="page">
      <div className="page-head">
        <h1>{t('Site administration')}</h1>
      </div>
      <ErrorBox error={overview.error} />
      {o && (
        <div className="op-stats">
          {[
            [t('Teams'), o.teams],
            [t('Users'), o.users],
            [t('New in 7 days'), o.signups_7d],
            [t('New in 30 days'), o.signups_30d],
            [t('Active in 7 days'), o.active_7d],
            [t('Issues'), o.issues],
            [t('Files'), fmtBytes(o.storage_bytes)],
          ].map(([label, value]) => (
            <div key={String(label)} className="op-stat">
              <div className="op-value">{value}</div>
              <div className="muted small">{label}</div>
            </div>
          ))}
        </div>
      )}
      <nav className="subnav">
        <button className={`link ${tab === 'teams' ? 'active' : ''}`} onClick={() => setTab('teams')}>
          {t('Teams')}
        </button>
        <button className={`link ${tab === 'users' ? 'active' : ''}`} onClick={() => setTab('users')}>
          {t('Users')}
        </button>
      </nav>
      {tab === 'teams' ? <TeamsTable /> : <UsersTable />}
    </div>
  );
}

function TeamsTable() {
  const [q, setQ] = useState('');
  const { data, error } = useQuery({ queryKey: ['op-teams', q], queryFn: () => api.get<TeamRow[]>('/api/operator/teams' + qs({ q })) });
  return (
    <Panel title={t('Teams')} actions={<input type="search" placeholder={t('Search')} value={q} onChange={(e) => setQ(e.target.value)} />}>
      <ErrorBox error={error} />
      <div className="table-scroll">
        <table className="simple">
          <thead>
            <tr>
              <th>{t('Team')}</th>
              <th>{t('Owner')}</th>
              <th>{t('Members')}</th>
              <th>{t('Projects')}</th>
              <th>{t('Issues')}</th>
              <th>{t('Files')}</th>
              <th>{t('Created')}</th>
            </tr>
          </thead>
          <tbody>
            {data?.map((tm) => (
              <tr key={tm.id}>
                <td>
                  {tm.name} <span className="muted small">#{tm.id}</span>
                </td>
                <td>
                  {tm.owner ?? '—'}
                  {tm.owner_email && <div className="muted small">{tm.owner_email}</div>}
                </td>
                <td>{tm.members}</td>
                <td>{tm.projects}</td>
                <td>{tm.issues}</td>
                <td className="nowrap">
                  {fmtBytes(tm.storage_bytes)}
                </td>
                <td className="nowrap">{fmtDate(tm.created_at, false)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function UsersTable() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const { data, error } = useQuery({ queryKey: ['op-users', q], queryFn: () => api.get<UserRow[]>('/api/operator/users' + qs({ q })) });
  const toggle = useMutation({
    mutationFn: (u: UserRow) => api.post(`/api/operator/users/${u.id}/enabled`, { enabled: !u.enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['op-users'] }),
  });
  return (
    <Panel title={t('Users')} actions={<input type="search" placeholder={t('Search')} value={q} onChange={(e) => setQ(e.target.value)} />}>
      <ErrorBox error={error || toggle.error} />
      <div className="table-scroll">
        <table className="simple">
          <thead>
            <tr>
              <th>{t('Username')}</th>
              <th>{t('E-mail')}</th>
              <th>{t('Teams')}</th>
              <th>{t('Created')}</th>
              <th>{t('Last Visit')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data?.map((u) => (
              <tr key={u.id} className={u.enabled ? '' : 'disabled'}>
                <td>
                  {u.username}
                  {u.site_admin && <span className="pill">{t('operator')}</span>}
                  {u.realname && <div className="muted small">{u.realname}</div>}
                </td>
                <td>
                  {u.email}
                  {u.email && !u.email_confirmed && <span className="pill">{t('not confirmed')}</span>}
                </td>
                <td>{u.teams}</td>
                <td className="nowrap">{fmtDate(u.created_at, false)}</td>
                <td className="nowrap">{fmtDate(u.last_visit)}</td>
                <td>
                  <button
                    className={`link ${u.enabled ? 'danger' : ''}`}
                    onClick={() => (u.enabled ? confirm(t('Disable {user}? They are logged out and cannot log in.', { user: u.username })) : true) && toggle.mutate(u)}
                  >
                    {u.enabled ? t('Disable') : t('Enable')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}
